import classNames from 'classnames';
import { Dispatch, type JSX, SetStateAction, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import CommunityConnectRelationshipAction from '@alltrails/amplitude/enums/CommunityConnectRelationshipAction';
import logCommunityConnectActionSelected from '@alltrails/amplitude/events/logCommunityConnectActionSelected';
import { getRelationshipAction } from '@alltrails/amplitude/utils/community/analyticsHelpers';
import { getUrlFromSlug } from '@alltrails/cards/utils/cardLinks';
import { userHasPermission } from '@alltrails/context/hooks/useAuthorization';
import Typography from '@alltrails/core/components/Typography';
import useLocalizedText from '@alltrails/core/hooks/useLocalizedText';
import compareUserIds from '@alltrails/core/utils/compareUserIds';
import Link from '@alltrails/denali/components/Link';
import StatusBadge from '@alltrails/denali/components/StatusBadge';
import Tag from '@alltrails/denali/components/Tag';
import First from '@alltrails/denali/icons/First';
import PlaceTrail from '@alltrails/denali/icons/PlaceTrail';
import type { LightboxPhoto, LightboxUser } from '@alltrails/denali/types';
import { getTrailDifficultyLabelFromNumber } from '@alltrails/difficulty/utils/trailDifficultyHelpers';
import LocalizationButton from '@alltrails/layout/components/LocalizationButton';
import { UserConnectionActionType } from '@alltrails/types/belay';
import type Context from '@alltrails/types/Context';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type { Review } from '@alltrails/types/Review';
import type { ReviewPhoto } from '@alltrails/types/ReviewPhoto';
import type SuccessToast from '@alltrails/types/SuccessToast';
import type { User } from '@alltrails/types/User';
import sanitizeReviewCommentHtml from '../../utils/sanitizeReviewCommentHtml';
import ReviewActionSection from '../ReviewActionSection';
import styles from './styles/styles.module.scss';
import TrailReviewOverflowMenu from './TrailReviewOverflowMenu';
import TrailReviewPhotos from './TrailReviewPhotos';
import TrailReviewReply from './TrailReviewReply';
import TrailReviewStructuredData from './TrailReviewStructuredData';

export type TrailReviewProps = {
  context?: Context;
  currentUser: User | null;
  hideBadges?: boolean;
  hideObstacles?: boolean;
  hideReplies?: boolean;
  index?: number;
  isEditable?: boolean;
  languageRegionCode: LanguageRegionCode;
  label: React.ReactNode;
  onActivityLinkClick?: () => void;
  onDeleteClick?: () => void;
  onEditClick?: () => void;
  onLightboxClose?: () => void;
  onLightboxPhotoViewed?: (review: Review, photo: LightboxPhoto['photo']) => void;
  onLightboxUserClick?: (user: LightboxUser) => void;
  onReviewPhotosScroll?: (review: Review, numPhotos: number) => void;
  onReviewPhotosScrollEnd?: (review: Review, numPhotos: number) => void;
  onReplyDeleteClick?: () => void;
  onReplySaveClick?: (replyText: string) => Promise<void>;
  onReviewPhotoClick?: (review: Review, photo: ReviewPhoto) => void;
  photosClassName?: string;
  preferHTML?: boolean;
  review: Review;
  localizeOnLoad?: boolean;
  setIsReportingModalOpen?: Dispatch<SetStateAction<boolean>>;
  setReportingSuccessToast?: Dispatch<SetStateAction<SuccessToast>>;
  onToggleSummaryExclusion?: (summaryExclusion: boolean) => void;
  photosOverflowOnMobile?: boolean;
  bottomLinkType?: 'trail' | 'activity';
  onTrailLinkClick?: () => void;
  showVoteButton?: boolean;
};

const TrailReview = ({
  index,
  currentUser,
  hideBadges,
  context,
  hideObstacles,
  hideReplies,
  isEditable,
  languageRegionCode,
  label,
  onActivityLinkClick,
  onDeleteClick,
  onEditClick,
  onLightboxClose,
  onLightboxPhotoViewed,
  onLightboxUserClick,
  onReviewPhotosScroll,
  onReviewPhotosScrollEnd,
  onReplyDeleteClick,
  onReplySaveClick,
  onReviewPhotoClick,
  photosClassName,
  preferHTML,
  review,
  localizeOnLoad = false,
  setIsReportingModalOpen,
  setReportingSuccessToast,
  onToggleSummaryExclusion,
  photosOverflowOnMobile = true,
  bottomLinkType = 'activity',
  onTrailLinkClick,
  showVoteButton
}: TrailReviewProps) => {
  const intl = useIntl();
  let localizeOptions;
  if (localizeOnLoad) {
    localizeOptions = {
      targetLangText: review?.comment,
      localizeByDefault: true
    };
  }
  const sourceText = review?.comment_source || review?.comment;
  const { canBeLocalized, toggleText, isLocalized, text } = useLocalizedText(
    sourceText,
    review?.comment_lang,
    languageRegionCode,
    {
      table: 'reviews',
      row: review?.id,
      column: 'comment'
    },
    localizeOptions
  );

  const [showReplyEditing, setShowReplyEditing] = useState(false);
  const [isSavingReply, setIsSavingReply] = useState(false);
  const [isMoreOptionsOpen, setIsMoreOptionsOpen] = useState(false);
  const difficultyText = getTrailDifficultyLabelFromNumber(intl, review?.difficulty || 0);

  let linkToRecording: string | null = null;
  if (
    review.associatedRecording &&
    review.user &&
    (userHasPermission(currentUser, 'trails:manage') || review.associatedRecording.contentPrivacy !== 'private')
  ) {
    linkToRecording = `/explore/recording/${review.associatedRecording.slug}`;
  }

  const shouldDisplayFirstReview = review.badges?.first;
  const shouldDisplaySummaryExclusion = userHasPermission(currentUser, 'trails:manage') && review.summaryExclusion;
  const shouldDisplayBadges: boolean = !hideBadges && (review.badges?.first || !!shouldDisplaySummaryExclusion);
  let firstReviewDiv: JSX.Element | null = null;
  if (shouldDisplayBadges && shouldDisplayFirstReview) {
    firstReviewDiv = (
      <div className={styles.badgeContainer}>
        <First color="--color-text-primary" size="sm" />
        <span>
          <FormattedMessage defaultMessage="First to review" />
        </span>
      </div>
    );
  }

  let summaryExclusionBadge: JSX.Element | null = null;
  if (shouldDisplayBadges && shouldDisplaySummaryExclusion) {
    summaryExclusionBadge = (
      <StatusBadge
        className={styles.summaryExclusion}
        variant="info"
        text={intl.formatMessage({ defaultMessage: 'Review not included in summary' })}
        emphasis="weak"
        size="sm"
      />
    );
  }

  let commentDiv: JSX.Element | null = null;
  if (preferHTML && review.comment_html && !canBeLocalized) {
    commentDiv = <p className={styles.reviewText} dangerouslySetInnerHTML={{ __html: sanitizeReviewCommentHtml(review.comment_html) }} />;
  } else {
    commentDiv = <p className={styles.reviewText}>{text}</p>;
  }

  const logConnectActionSelected = (relationshipAction: CommunityConnectRelationshipAction) => {
    logCommunityConnectActionSelected({
      relationship_action: relationshipAction,
      carousel_position: -1, // never in carousel, -1 for "null" value
      item_location: 'trail page review',
      item_position: index,
      source_user_id: `${currentUser?.id}`,
      target_user_id: `${review.user.id}`
    });
  };

  const onConnectClick = (actionType: UserConnectionActionType) => {
    logConnectActionSelected(getRelationshipAction(actionType));
  };

  const shouldRenderAttributes = !hideObstacles && (review.obstacles.length > 0 || review.infoAttributes?.length > 0 || review.difficulty);
  const includeReviewMeta = !!review.trailName && !!review.rating;

  return (
    <div>
      {includeReviewMeta && <TrailReviewStructuredData review={review} localizedText={text} languageRegionCode={languageRegionCode} />}
      <div className={styles.reviewUser}>
        <div className={styles.reviewUserLabel}>{label}</div>
        {currentUser && setIsReportingModalOpen && (
          <div className={styles.reviewUserMenu}>
            <TrailReviewOverflowMenu
              isThirdPartyReview={!compareUserIds(review.user.id, currentUser?.id)}
              isMoreOptionsOpen={isMoreOptionsOpen}
              setIsMoreOptionsOpen={setIsMoreOptionsOpen}
              onConnectClick={onConnectClick}
              setIsReportingModalOpen={setIsReportingModalOpen}
              canComment={userHasPermission(currentUser, 'trails:manage') && !compareUserIds(review.user.id, currentUser?.id)}
              hideReplies={hideReplies}
              isEditable={isEditable}
              isAdmin={userHasPermission(currentUser, 'trails:manage')}
              onCommentClick={() => setShowReplyEditing(true)}
              onDeleteClick={onDeleteClick}
              onEditClick={onEditClick}
              setReportingSuccessToast={setReportingSuccessToast}
              summaryExclusion={review.summaryExclusion}
              onToggleSummaryExclusion={onToggleSummaryExclusion}
              reviewUserId={review.user.id}
            />
          </div>
        )}
      </div>
      {((review.ratingAttributes?.length ?? 0) > 0 || (review.difficultyAttributes?.length ?? 0) > 0) && (
        <div className={styles.ratingTags}>
          {review.ratingAttributes?.map(attribute => (
            <Tag key={attribute.uid} text={attribute.name} size="sm" />
          ))}
          {review.difficultyAttributes?.map(attribute => (
            <Tag key={attribute.uid} text={attribute.displayName} size="sm" />
          ))}
        </div>
      )}
      <div
        className={classNames(styles.reviewBody, {
          [styles.reviewBodyFirst]: shouldDisplayBadges
        })}
      >
        {summaryExclusionBadge}
        {firstReviewDiv}
        {commentDiv}
      </div>
      {!!canBeLocalized && <LocalizationButton className={styles.translationButton} showSource={!isLocalized} toggleShowSource={toggleText} />}
      <>
        {!!shouldRenderAttributes && (
          <div className={styles.attributes}>
            {review.obstacles.length > 0 && (
              <div>
                <span>
                  <Typography variant="text200bold" className={styles.attributeTitle}>
                    <FormattedMessage defaultMessage="Conditions:" />{' '}
                  </Typography>
                  <Typography variant="text200">{review.obstacles.map(obstacle => obstacle.name).join(', ')}</Typography>
                </span>
              </div>
            )}
            {review.infoAttributes?.length > 0 && (
              <div>
                <span>
                  <Typography variant="text200bold" className={styles.attributeTitle}>
                    <FormattedMessage defaultMessage="Parking:" />{' '}
                  </Typography>
                  <Typography variant="text200">{review.infoAttributes.map(attribute => attribute.name).join(', ')}</Typography>
                </span>
              </div>
            )}
            {!!review.difficulty && (
              <div>
                <span>
                  <Typography variant="text200bold" className={styles.attributeTitle}>
                    <FormattedMessage defaultMessage="Difficulty:" />{' '}
                  </Typography>
                  <Typography variant="text200">{difficultyText}</Typography>
                </span>
              </div>
            )}
          </div>
        )}
        {review.photos && !!review.photos.length && (
          <TrailReviewPhotos
            className={classNames(photosClassName, styles.newPhotoCarousel, { [styles.photosOverflowOnMobile]: photosOverflowOnMobile })}
            languageRegionCode={languageRegionCode}
            onLightboxClose={onLightboxClose}
            onLightboxPhotoViewed={onLightboxPhotoViewed}
            onLightboxUserClick={onLightboxUserClick}
            onReviewPhotosScroll={onReviewPhotosScroll}
            onReviewPhotosScrollEnd={onReviewPhotosScrollEnd}
            onReviewPhotoClick={onReviewPhotoClick}
            review={review}
            context={context}
            linkToActivity={linkToRecording || undefined}
          />
        )}
        <TrailReviewReply
          hideReplies={hideReplies}
          isAdmin={userHasPermission(currentUser, 'trails:manage')}
          isEditing={showReplyEditing}
          isSaving={isSavingReply}
          onCancel={() => setShowReplyEditing(false)}
          onDelete={onReplyDeleteClick}
          onSave={async (replyText: string) => {
            setIsSavingReply(true);
            await onReplySaveClick?.(replyText);
            setShowReplyEditing(false);
            setIsSavingReply(false);
          }}
          reviewReply={review.replies?.[0]} // only show 1 reply
        />
        {bottomLinkType === 'trail' && (
          <Link href={getUrlFromSlug(`trail/${review.trailSlug}`, '')} target="_blank" onClick={onTrailLinkClick} className={styles.bottomLink}>
            <PlaceTrail size="sm" className={styles.locationIcon} />
            {review.trailName}
          </Link>
        )}
        <ReviewActionSection
          review={review}
          showVoteButton={showVoteButton}
          activityLink={bottomLinkType === 'activity' && linkToRecording ? { href: linkToRecording, onClick: onActivityLinkClick } : undefined}
        />
      </>
    </div>
  );
};

export default TrailReview;
